/**
 * Lock page scroll while cart is open using body position:fixed.
 * Unlock clears body styles and restores scroll position.
 */
const CartScrollLock = {
  y: 0,
  locked: false,

  lock() {
    if (this.locked) return;
    this.locked = true;
    this.y = window.scrollY || window.pageYOffset || 0;

    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${this.y}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
  },

  unlock() {
    const body = document.body;
    const html = document.documentElement;
    // Detect a lock even if the in-memory flag is stale (e.g. the drawer was
    // dismissed by an external layer such as GoKwik without the theme's close
    // handler running). Mobile browsers keep the page frozen until these
    // inline styles are cleared, which is why scroll stayed stuck until reload.
    const hadLock = this.locked || body.style.position === "fixed";
    const y = this.y || Math.abs(parseInt(body.style.top || "0", 10)) || 0;

    this.locked = false;
    this.y = 0;

    // Clear body pin
    body.style.removeProperty("position");
    body.style.removeProperty("top");
    body.style.removeProperty("left");
    body.style.removeProperty("right");
    body.style.removeProperty("width");

    // Clear any leftover pin styles from older experiments
    document.querySelectorAll('[data-cart-scroll-pinned="1"]').forEach((el) => {
      el.style.removeProperty("position");
      el.style.removeProperty("top");
      el.style.removeProperty("left");
      el.style.removeProperty("width");
      el.style.removeProperty("right");
      el.style.removeProperty("max-width");
      delete el.dataset.cartScrollPinned;
    });

    ["overflow", "touch-action", "height"].forEach((prop) => {
      body.style.removeProperty(prop);
      html.style.removeProperty(prop);
    });
    body.classList.remove(
      "cart-scroll-locked",
      "cart-open-scroll-lock",
      "overflow-hidden",
    );
    html.classList.remove(
      "cart-scroll-locked",
      "cart-open-scroll-lock",
      "overflow-hidden",
    );

    // Only restore the scroll position if we actually had the page pinned.
    // Restore across several frames because mobile browsers can ignore a
    // scrollTo issued in the same tick that position:fixed is removed.
    if (hadLock) {
      window.scrollTo(0, y);
      requestAnimationFrame(() => window.scrollTo(0, y));
      setTimeout(() => window.scrollTo(0, y), 0);
    }
  },
};

/**
 * Self-healing scroll-lock guardian.
 *
 * The theme locks scroll when the cart drawer opens and unlocks it from
 * `closeDrawer()`. On mobile a dismissal can bypass that handler (for example
 * the GoKwik layer hiding the drawer), leaving the page pinned until reload.
 * This watches the real open/visible state of the drawer and reconciles the
 * lock, so scroll is always restored once the drawer is no longer on screen —
 * regardless of how it was closed.
 */
function cartDrawerIsOpenOnScreen() {
  const drawer = document.querySelector("cart-drawer");
  if (!drawer) return false;

  const stateOpen =
    drawer.hasAttribute("open") ||
    document.body.classList.contains("page-overlay-cart-on") ||
    !!drawer.querySelector(".wt-cart__drawer--open");
  if (!stateOpen) return false;

  const panel = drawer.querySelector(".wt-cart__drawer");
  if (!panel) return true;

  const cs = window.getComputedStyle(panel);
  if (
    cs.display === "none" ||
    cs.visibility === "hidden" ||
    parseFloat(cs.opacity) === 0
  ) {
    return false;
  }

  // A closed drawer is translated off the right edge; treat off-screen or
  // zero-size panels as closed even if a stale open class/attribute lingers.
  const rect = panel.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0
  );
}

function syncCartScrollLock() {
  if (cartDrawerIsOpenOnScreen()) {
    if (!CartScrollLock.locked) CartScrollLock.lock();
  } else if (
    CartScrollLock.locked ||
    document.body.style.position === "fixed"
  ) {
    CartScrollLock.unlock();
  }
}

if (!window.__cartScrollLockGuardInstalled) {
  window.__cartScrollLockGuardInstalled = true;

  let scheduled = false;
  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      syncCartScrollLock();
    });
  };

  const bodyObserver = new MutationObserver(scheduleSync);
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
  });

  const attachDrawerWatchers = () => {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || drawer.dataset.scrollGuard === "1") return;
    drawer.dataset.scrollGuard = "1";

    new MutationObserver(scheduleSync).observe(drawer, {
      attributes: true,
      attributeFilter: ["open", "class"],
      subtree: true,
    });

    const panel = drawer.querySelector(".wt-cart__drawer");
    if (panel) panel.addEventListener("transitionend", scheduleSync);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachDrawerWatchers);
  } else {
    attachDrawerWatchers();
  }

  // Restore scroll after bfcache navigation (mobile back button).
  window.addEventListener("pageshow", () => {
    if (!cartDrawerIsOpenOnScreen()) CartScrollLock.unlock();
  });

  if (typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartDrawerClose) {
    document.addEventListener(PUB_SUB_EVENTS.cartDrawerClose, () =>
      CartScrollLock.unlock(),
    );
  }
}

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    CartScrollLock.unlock();
    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    CartScrollLock.unlock();
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  openDrawer() {
    if (this.isOpen) {
      CartScrollLock.lock();
      return;
    }

    this.drawer = this.querySelector(`.${this.drawerClass}`) || this.drawer;
    this.setAttribute("open", "");
    this.isOpen = true;
    this.drawer?.classList.add(this.classDrawerActive);
    this.body.classList.add(this.activeOverlayBodyClass);
    CartScrollLock.lock();
    this.temporaryHideFocusVisible();
    setTabindex(this.toggleEelements(), "0");

    document.dispatchEvent(
      new CustomEvent(PUB_SUB_EVENTS.cartDrawerOpen, { bubbles: true }),
    );
  }

  closeDrawer() {
    this.drawer = this.querySelector(`.${this.drawerClass}`) || this.drawer;
    this.removeAttribute("open");
    this.isOpen = false;
    this.drawer?.classList.remove(this.classDrawerActive);
    this.body.classList.remove(this.activeOverlayBodyClass);
    CartScrollLock.unlock();
    this.temporaryHideFocusVisible();
    setTabindex(this.toggleEelements(), "-1");

    document.dispatchEvent(
      new CustomEvent(PUB_SUB_EVENTS.cartDrawerClose, { bubbles: true }),
    );
  }

  toggleDrawerClasses() {
    if (this.isOpen || this.hasAttribute("open")) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) this.closeDrawer();
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.closest(".wt-cart__drawer__close")) {
        e.preventDefault();
        this.closeDrawer();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    this.drawer = this.querySelector(`.${this.drawerClass}`) || this.drawer;

    if (isClosedCart) {
      setTimeout(() => this.openDrawer(), 0);
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
